import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import Panel from '~/components/Panel';
import PatientVerificationForm from '~/components/PatientVerificationForm';
import { fetchPatientForVerification } from '~/services/patient-verification';
import { downloadMedicalVerificationPdf } from '~/utils/medicalVerificationPdf';
import { formatDateIfValid } from '~/utils';

// mp_certify_identity values -> who gave consent (mirrors PatientVerificationDrawer).
const IDENTITY_LABELS: Record<number, string> = {
    1: 'Client',
    2: 'Spouse',
    3: 'Parent of minor',
    4: 'Legal Guardian',
    5: 'POA',
};

const VerifyClient = () => {
    const { patientId } = useParams<{ patientId: string }>();
    const navigate = useNavigate();
    const [patient, setPatient] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (!patientId) return;
        const id = Number(patientId);
        let cancelled = false;
        (async () => {
            try {
                const patientResp = await fetchPatientForVerification(id);
                if (cancelled) return;
                setPatient(patientResp);
            } catch (e) {
                if (!cancelled) setLoadFailed(true);
                console.error('Verify load failed:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [patientId]);

    if (loading) {
        return (
            <>
                <Breadcrumb pageName='Verify Client' />
                <Panel>
                    <p className='text-[14px] text-[#949599]'>Loading…</p>
                </Panel>
            </>
        );
    }
    if (loadFailed || !patient) {
        return (
            <>
                <Breadcrumb pageName='Verify Client' />
                <Panel>
                    <p className='text-[14px] text-[#949599]'>
                        Could not load this client.
                    </p>
                </Panel>
            </>
        );
    }

    const person = patient.person ?? {};
    const fullName = `${person.first_name ?? ''} ${
        person.last_name ?? ''
    }`.trim();
    const primaryPhone =
        person.phone_set?.find((p: any) => p?.phone)?.phone ?? person.phone;
    const medicalNeed = patient.primary_medical_issue?.name ?? '';

    // Client consent comes from the campaign application (mirrors the CS
    // verification drawer). The patient endpoint returns just the consent
    // fields as `application`, or null when there's no post-compliance one.
    const application = patient.application;
    const consentName = (application?.mp_signature ?? '').toString().trim();
    const consentRelationship =
        IDENTITY_LABELS[Number(application?.mp_certify_identity)] ?? '';
    const hasConsent = !!consentName || !!consentRelationship;

    const handleDownloadConsent = async () => {
        try {
            // No campaign object on this screen; the download filename falls
            // back to the client's name (see downloadMedicalVerificationPdf).
            await downloadMedicalVerificationPdf(undefined, patient, application);
        } catch (e) {
            console.error('Failed to download consent form:', e);
            toast.error('Failed to download consent form');
        }
    };

    return (
        <>
            <Breadcrumb pageName='Verify Client' />

            <div className='grid grid-cols-12 gap-7.5'>
                <div className='col-span-12 mb-7.5 xl:col-span-8'>
                    <Panel>
                        <div>
                            <h2 className='text-[24px]/[28px] font-bold'>
                                {fullName}
                            </h2>
                            <div className='mt-2 text-[14px]/[20px]'>
                                {person.address_1}
                                {person.address_2 ? (
                                    <>
                                        <br />
                                        {person.address_2}
                                    </>
                                ) : null}
                                <br />
                                {person.city}
                                {person.city ? ', ' : ''}
                                {person.state ?? ''} {person.zip_code ?? ''}
                            </div>
                            <div className='mt-2 text-[14px]/[20px]'>
                                <div>
                                    <span className='text-[#949599]'>
                                        Phone:
                                    </span>{' '}
                                    {primaryPhone ?? '—'}
                                </div>
                                <div>
                                    <span className='text-[#949599]'>
                                        Email:
                                    </span>{' '}
                                    {person.email ?? '—'}
                                </div>
                            </div>
                        </div>

                        <hr className='my-6' />

                        {/* Closed campaigns stay viewable but can't be
                            verified by a med pro (HHLTB-2914). */}
                        {!patient.verification_completion_date &&
                        patient.can_verify === false ? (
                            <p className='text-[14px]/[20px]'>
                                This campaign isn't open, so it can't be
                                verified. Please contact Help Hope Live with any
                                questions.
                            </p>
                        ) : (
                            <PatientVerificationForm
                                patient={patient}
                                onVerified={setPatient}
                                onCancel={() => navigate(-1)}
                            />
                        )}
                    </Panel>
                </div>

                <div className='col-span-12 mb-7.5 xl:col-span-4'>
                    <Panel>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Gender
                            </label>
                            <div>{person.gender ?? '—'}</div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Date of Birth
                            </label>
                            <div>
                                {person.birth_date
                                    ? formatDateIfValid(
                                          person.birth_date,
                                          'MM/dd/yyyy'
                                      )
                                    : '—'}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Medical Need
                            </label>
                            <div>{medicalNeed || '—'}</div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Client Consent
                            </label>
                            {hasConsent ? (
                                <>
                                    <div>
                                        {consentName
                                            ? `Given by ${consentName}`
                                            : 'Given'}
                                        {consentRelationship
                                            ? `, ${consentRelationship}`
                                            : ''}
                                    </div>
                                    {application && (
                                        <button
                                            type='button'
                                            onClick={handleDownloadConsent}
                                            className='mt-1 inline-flex items-center gap-1 text-[14px] text-primary hover:opacity-80'
                                        >
                                            <svg
                                                xmlns='http://www.w3.org/2000/svg'
                                                width='16'
                                                height='16'
                                                viewBox='0 0 24 24'
                                                fill='none'
                                                stroke='currentColor'
                                                strokeWidth='2'
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                            >
                                                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                                                <polyline points='7 10 12 15 17 10' />
                                                <line x1='12' y1='15' x2='12' y2='3' />
                                            </svg>
                                            Download Consent Form
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div>—</div>
                            )}
                        </div>
                    </Panel>
                </div>
            </div>
        </>
    );
};

export default VerifyClient;
